import type { CSSProperties, FC, ReactNode } from 'react'
import type { ChartAnimation } from '@/components/charts/chart-utils'

import { useMemo } from 'react'
import { cn } from '@/lib/utils'

import {
    areaPath,
    domainOf,
    linePath,
    toPoints,
    useChartId
} from '@/components/charts/chart-utils'

interface SparklineProps {
    values: Array<number>
    color: string
    height?: number
    fill?: boolean
    animation?: ChartAnimation
    delay?: number
    showEnd?: boolean
    className?: string
}

/** Tiny trend line for KPI tiles. Decorative: the number beside it carries the meaning. */
export const Sparkline: FC<SparklineProps> = ({
    values,
    color,
    height = 36,
    fill = true,
    animation = 'inview',
    delay = 0,
    showEnd = true,
    className
}): ReactNode => {
    const id = useChartId('spark')

    const { line, area, end } = useMemo(() => {
        const points = toPoints(values, domainOf([values], 0.18))

        return {
            line: linePath(points),
            area: areaPath(points),
            end: points[points.length - 1]
        }
    }, [values])

    return (
        <div
            aria-hidden='true'
            className={cn('relative', animation === 'autoplay' && 'chart-autoplay', className)}
            data-chart={animation === 'manual' ? undefined : ''}
            data-inview={animation === 'autoplay' ? 'true' : undefined}
            style={{ height, '--chart-delay': `${delay}ms` } as CSSProperties}
        >
            <div className='chart-wipe absolute inset-0' data-chart-wipe=''>
                <svg viewBox='0 0 100 100' preserveAspectRatio='none' className='absolute inset-0 size-full overflow-visible'>
                    <defs>
                        <linearGradient id={`${id}-fill`} x1='0' y1='0' x2='0' y2='1'>
                            <stop offset='0%' stopColor={color} stopOpacity={0.22} />
                            <stop offset='100%' stopColor={color} stopOpacity={0} />
                        </linearGradient>
                    </defs>

                    {fill && <path d={area} fill={`url(#${id}-fill)`} />}

                    <path
                        d={line}
                        fill='none'
                        stroke={color}
                        strokeWidth={2}
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        vectorEffect='non-scaling-stroke'
                    />
                </svg>

                {showEnd && end && (
                    <span
                        className='absolute size-2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-ivory'
                        style={{ left: `${end[0]}%`, top: `${end[1]}%`, backgroundColor: color }}
                    />
                )}
            </div>
        </div>
    )
}
